import { steepnessColorForPercent } from './steepnessGradient';
import { surfaceForValue } from './surfaces';
import { surfaceGroupForValue, DEFAULT_SURFACE_GROUP_COLOR } from './surfaceGroups';

export interface PinnedReadout {
  routeName: string | null;
  distanceLabel: string;
  elevationLabel: string;
  gradeLabel: string | null;
  gradeColor: string;
  surfaceLabel: string | null;
  surfaceColor: string;
}

export interface PinnedReadoutChartData {
  points: Array<{ x: number; y: number }>;
  gradients?: (number | undefined)[];
  surfaces?: (number | undefined)[];
}

export interface PinnedReadoutOptions {
  unitLabels: { distance: string; elevation: string };
  tooltipBeforeLabel?: ((index: number) => string | null) | null;
  /** Colour the line falls back to for a point with no grade data. */
  fallbackLineColor: string;
  /** Whether the line is coloured by surface group (ElevationProfile's hasSurfaceLine). */
  hasSurfaceLine: boolean;
}

/**
 * Same facts ElevationProfile.vue's Chart.js tooltip callbacks show
 * (Distance/Grade/Surface/Elevation, plus the optional route-name line),
 * computed independently of Chart.js so `pinnedTooltip` mode's readout tile
 * doesn't depend on the tooltip plugin being enabled. Pure — no Vue, no
 * Chart.js — so it's unit-testable without mounting the component.
 */
export function buildPinnedReadout(
  index: number,
  chartData: PinnedReadoutChartData,
  opts: PinnedReadoutOptions
): PinnedReadout | null {
  const point = chartData.points[index];
  if (!point) return null;

  const grade = chartData.gradients?.[index];
  const surfaceValue = chartData.surfaces?.[index];

  return {
    routeName: opts.tooltipBeforeLabel?.(index) || null,
    distanceLabel: `${point.x} ${opts.unitLabels.distance}`,
    elevationLabel: `${point.y}${opts.unitLabels.elevation}`,
    gradeLabel: grade != null ? `${Math.round(grade) > 0 ? '+' : ''}${Math.round(grade)}%` : null,
    gradeColor: grade != null ? steepnessColorForPercent(grade) : opts.fallbackLineColor,
    surfaceLabel: surfaceValue != null ? surfaceForValue(surfaceValue).name : null,
    surfaceColor: opts.hasSurfaceLine ? surfaceGroupForValue(surfaceValue).color : DEFAULT_SURFACE_GROUP_COLOR,
  };
}
